import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  Divider,
  Chip,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';
import { useAvailableActions } from '../../../hooks/useAvailableActions';

/** UTC offsets in 30-minute increments, from -12:00 to +14:00, expressed in seconds. */
const TIMEZONE_OFFSET_OPTIONS = Array.from({ length: ((14 - -12) * 2) + 1 }, (_, i) => {
  const totalMinutes = -12 * 60 + i * 30;
  const sign = totalMinutes < 0 ? '-' : '+';
  const absMinutes = Math.abs(totalMinutes);
  const hh = String(Math.floor(absMinutes / 60)).padStart(2, '0');
  const mm = String(absMinutes % 60).padStart(2, '0');
  return {
    seconds: totalMinutes * 60,
    label: `UTC${sign}${hh}:${mm}`,
  };
});

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const ORDINAL_NAMES = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

function formatWeekTime(weekTime) {
  if (!weekTime) return 'N/A';
  const ordinal = weekTime.ordinal === -1 ? 'Last' : ORDINAL_NAMES[weekTime.ordinal] ?? weekTime.ordinal;
  const month = MONTH_NAMES[weekTime.month] ?? weekTime.month;
  const weekday = WEEKDAY_NAMES[weekTime.weekday] ?? weekTime.weekday;
  const hh = String(weekTime.hour).padStart(2, '0');
  const mm = String(weekTime.minute).padStart(2, '0');
  return `${ordinal} ${weekday} of ${month}, ${hh}:${mm}`;
}

const closestOffsetOption = (seconds) => {
  if (seconds == null) return TIMEZONE_OFFSET_OPTIONS.find((o) => o.seconds === 0)?.seconds ?? 0;
  let closest = TIMEZONE_OFFSET_OPTIONS[0];
  let closestDiff = Math.abs(TIMEZONE_OFFSET_OPTIONS[0].seconds - seconds);
  for (const option of TIMEZONE_OFFSET_OPTIONS) {
    const diff = Math.abs(option.seconds - seconds);
    if (diff < closestDiff) {
      closest = option;
      closestDiff = diff;
    }
  }
  return closest.seconds;
};

export default function TimeSettingsTab({ deviceId, siteId, accountId, token }) {
  const { enqueueSnackbar } = useSnackbar();
  const { canEditSites } = useAvailableActions();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);
  const [syncWithServer, setSyncWithServer] = useState(true);
  const [manualDeviceTime, setManualDeviceTime] = useState(null);

  useEffect(() => {
    if (deviceId && token) {
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, token]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cloudApi.getFaceScannerTimeSettings(token, siteId, accountId, deviceId);
      const data = response.data;
      if (data) {
        setSettings(data);
        setSyncWithServer(!!data.syncWithServer);
        setManualDeviceTime(data.deviceTimeUtc ? dayjs(data.deviceTimeUtc) : null);
      }
    } catch (err) {
      console.error('Error loading time settings:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to load time settings';
      setError(errorMessage);
      enqueueSnackbar(typeof errorMessage === 'string' ? errorMessage : 'Failed to load time settings', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cloudApi.setFaceScannerTimeSettings(token, siteId, accountId, {
        deviceId,
        // Timezone is no longer user-editable; send back the value as last read from the device.
        timeZoneOffsetSeconds: settings?.timeZoneOffsetSeconds,
        syncWithServer,
        deviceTimeUtc: !syncWithServer && manualDeviceTime ? dayjs(manualDeviceTime).toISOString() : null,
        dstSchedules: settings?.dstSchedules || [],
      });
      enqueueSnackbar('Time settings saved successfully', { variant: 'success' });
      await loadSettings();
    } catch (err) {
      console.error('Error saving time settings:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to save time settings';
      setError(errorMessage);
      enqueueSnackbar(typeof errorMessage === 'string' ? errorMessage : 'Failed to save time settings', {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentDeviceTimeLabel = useMemo(() => {
    if (!settings?.deviceTimeUtc) return 'N/A';
    return `${dayjs(settings.deviceTimeUtc).format('YYYY-MM-DD HH:mm:ss')} (UTC, as last read)`;
  }, [settings?.deviceTimeUtc]);

  const currentTimezoneOffsetLabel = useMemo(() => {
    if (settings?.timeZoneOffsetSeconds == null) return 'N/A';
    const seconds = closestOffsetOption(settings.timeZoneOffsetSeconds);
    return TIMEZONE_OFFSET_OPTIONS.find((option) => option.seconds === seconds)?.label || 'N/A';
  }, [settings?.timeZoneOffsetSeconds]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Typography variant="h6">Device Clock</Typography>

        <TextField fullWidth label="Current Device Time" value={currentDeviceTimeLabel} disabled />

        <TextField
          fullWidth
          label="Timezone Offset (as configured on device)"
          value={currentTimezoneOffsetLabel}
          disabled
          helperText="Timezone is read-only here and can only be changed on the device itself."
        />

        <FormControlLabel
          control={
            <Switch
              checked={syncWithServer}
              onChange={(e) => setSyncWithServer(e.target.checked)}
              disabled={!canEditSites}
            />
          }
          label="Sync clock with server automatically"
        />

        {!syncWithServer && (
          <DateTimePicker
            label="Set Device Time (UTC)"
            value={manualDeviceTime}
            onChange={(value) => setManualDeviceTime(value)}
            disabled={!canEditSites}
            sx={{ width: '100%' }}
          />
        )}

        <Divider />

        <Typography variant="h6">DST Schedule</Typography>
        <Typography variant="body2" color="text.secondary">
          Daylight-saving schedule is currently read-only here; it is preserved as-is when saving other settings above.
        </Typography>

        {settings?.dstSchedules?.length ? (
          <Stack spacing={1}>
            {settings.dstSchedules.map((schedule, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`+${schedule.timeOffsetSeconds}s`} />
                <Typography variant="body2">
                  Starts: {formatWeekTime(schedule.startTime)} — Ends: {formatWeekTime(schedule.endTime)}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No DST schedule configured.
          </Typography>
        )}

        {canEditSites && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}>
            <Button variant="outlined" onClick={loadSettings} disabled={loading || saving}>
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || saving}
              startIcon={saving ? <CircularProgress size={20} /> : <Icon icon="solar:diskette-bold" />}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
